// UX-4a (UX2-A11Y-1; UX v1.7 §8 → WCAG 2.4.2 Page Titled) — one document-title
// rule for the whole office app. Before this every route read the static
// `index.html` title "Steier — Office", so tabs, history and screen-reader page
// announcements could not tell O-03 from O-15.
//
//   <page title> · <tenant name> — Steier        (inside the shell)
//   <page title> — Steier                         (sign-in; no tenant yet)
//
// The page title is the screen registry's `titleKey` (router.tsx wraps every
// registry route in <RouteTitle>), so the title cannot drift from the screen
// inventory. The tenant name is the SAME label the shell header shows
// (useShellBrand — moved here from app-shell.tsx, value unchanged).
import { useEffect, type ReactNode } from "react";
import { useSession } from "../auth/session-store";
import { resolveCurrentTenant } from "../auth/tenant";
import { brandLabel, STEIER_DEFAULT_BRANDING } from "../theme/branding";

/** The product suffix every title ends with (the platform wordmark). */
export const PRODUCT_TITLE = STEIER_DEFAULT_BRANDING.wordmark;

/** `<page> · <tenant> — Steier`; the tenant part is dropped when it is only the wordmark. */
export function documentTitle(pageTitle: string, tenantName?: string): string {
  const parts = [pageTitle, tenantName]
    .map((part) => part?.trim() ?? "")
    .filter((part) => part.length > 0 && part !== PRODUCT_TITLE);
  return `${parts.join(" · ")} — ${PRODUCT_TITLE}`;
}

/**
 * The shell's brand label (WP-22, D-F5): tenant emailDisplay > org name >
 * subdomain slug > platform wordmark. Shared by the header and the title.
 */
export function useShellBrand(): string {
  const org = useSession((s) => s.org);
  const tenant = resolveCurrentTenant();
  return brandLabel(
    org?.branding,
    org?.name,
    tenant.kind === "subdomain" ? tenant.slug : undefined,
  );
}

/** Set `document.title` while the calling component is mounted. */
export function useDocumentTitle(title: string): void {
  useEffect(() => {
    document.title = title;
  }, [title]);
}

/** Route wrapper: titles a shell route `<pageTitle> · <tenant> — Steier`. */
export function RouteTitle({ title, children }: { title: string; children: ReactNode }) {
  const tenant = useShellBrand();
  useDocumentTitle(documentTitle(title, tenant));
  return <>{children}</>;
}
